import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Logo } from "../components/Logo";
import { LanguageSwitcher } from "../components/LanguageSwitcher";
import { useI18n } from "../i18n/I18nContext";
import { useAuth } from "../contexts/AuthContext";
import { api, formatApiError } from "../lib/api";
import { ShieldCheck, KeyRound, ArrowLeft, Check } from "lucide-react";

const MFASetup = () => {
  const { t } = useI18n();
  const location = useLocation();
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const stateData = location.state || {};
  const challenge = stateData.challenge_token;
  const [setup, setSetup] = useState(null);
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!challenge) {
      navigate("/login", { replace: true });
      return;
    }
    (async () => {
      try {
        const { data } = await api.post("/auth/mfa/setup-init", {
          challenge_token: challenge,
        });
        setSetup(data);
      } catch (err) {
        setError(formatApiError(err.response?.data?.detail) || err.message);
      }
    })();
  }, [challenge, navigate]);

  const onVerify = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/mfa/setup-verify", {
        challenge_token: challenge,
        code: code.trim(),
      });
      setUser(data.user);
      navigate("/dashboard", { replace: true });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white grid-bg flex flex-col">
      <header className="border-b border-white/[0.08]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 h-16 flex items-center justify-between">
          <Link to="/" data-testid="mfa-home-link"><Logo size={28} /></Link>
          <LanguageSwitcher />
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-xl">
          <div className="card-sharp p-8 lg:p-10" data-testid="mfa-setup-card">
            <div className="flex items-center gap-2.5 text-[#19A7E0]">
              <ShieldCheck className="w-4 h-4" />
              <p className="tactical-label">MFA · Setup</p>
            </div>
            <h1 className="mt-4 text-2xl lg:text-3xl tracking-tight font-medium">
              {t("auth.mfaSetupTitle")}
            </h1>
            <p className="mt-3 text-sm text-white/60 leading-relaxed">
              {t("auth.mfaSetupSubtitle")}
            </p>

            {setup ? (
              <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
                <div className="bg-white p-3 rounded-sm">
                  <img
                    src={setup.qr_data_url}
                    alt="QR MFA"
                    className="w-full h-auto block"
                    data-testid="mfa-qr-image"
                  />
                </div>
                <div>
                  <p className="text-[11px] font-mono uppercase tracking-[0.2em] text-white/50">
                    {t("auth.secretLabel")}
                  </p>
                  <p
                    className="mt-2 font-mono text-sm bg-[#121620] border border-white/10 rounded-sm px-3 py-2.5 break-all"
                    data-testid="mfa-secret"
                  >
                    {setup.secret}
                  </p>
                </div>
              </div>
            ) : (
              <p className="mt-8 tactical-label" data-testid="mfa-setup-loading">cargando…</p>
            )}

            <form onSubmit={onVerify} className="mt-8 space-y-5" data-testid="mfa-setup-form">
              <div>
                <label className="text-[11px] font-mono uppercase tracking-[0.2em] text-white/50">
                  {t("auth.code")}
                </label>
                <div className="mt-2 relative">
                  <KeyRound className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
                  <input
                    inputMode="numeric"
                    pattern="\d*"
                    maxLength={6}
                    required
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                    data-testid="mfa-setup-code-input"
                    className="w-full bg-[#0A0D14] border border-white/15 focus:border-[#19A7E0] focus:ring-1 focus:ring-[#19A7E0] rounded-sm pl-10 pr-3 py-3 text-lg font-mono tracking-[0.4em] outline-none"
                    placeholder="000000"
                  />
                </div>
              </div>

              {error && (
                <p className="text-xs text-[#EF4444] bg-[#EF4444]/10 border border-[#EF4444]/30 rounded-sm px-3 py-2" data-testid="mfa-setup-error">
                  {error}
                </p>
              )}

              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => navigate("/login")}
                  data-testid="mfa-setup-back"
                  className="px-4 py-3 btn-ghost text-xs font-mono uppercase tracking-[0.18em] rounded-sm inline-flex items-center gap-2"
                >
                  <ArrowLeft className="w-3.5 h-3.5" /> {t("auth.back")}
                </button>
                <button
                  type="submit"
                  disabled={loading || code.length !== 6}
                  data-testid="mfa-setup-verify-btn"
                  className="flex-1 inline-flex items-center justify-center gap-2 btn-primary py-3 text-xs font-mono uppercase tracking-[0.18em] rounded-sm disabled:opacity-50"
                >
                  <Check className="w-4 h-4" /> {t("auth.verify")}
                </button>
              </div>
            </form>
          </div>
          <p className="mt-6 text-center text-[10px] font-mono uppercase tracking-[0.25em] text-white/30">
            © 2026 IN|STALROS Intelligence &amp; Integration
          </p>
        </div>
      </main>
    </div>
  );
};

export default MFASetup;
